import { Component, Input, Output, EventEmitter, OnChanges, SimpleChanges, OnDestroy, OnInit, Injector, ViewChild } from '@angular/core';
import { Observable, Subscription } from 'rxjs';
import { MatSort, Sort } from '@angular/material/sort';
import { FormStatusEnum, FormType } from 'src/app/common/enumerations/enumerations';
import { utils } from 'src/app/modules/libs/utils';
import { Form } from 'src/app/services/forms/form';
import { FormVersion } from 'src/app/services/forms/form-version';
import { FormService } from './form.service';
import { MatTableDataSource } from '@angular/material/table';
import { EbService } from 'src/app/components/eb/eb.service';
import { BaseComponent } from 'src/app/common/base/base.component';
import { PpstbService } from 'src/app/components/ppstb/ppstb.service';

@Component({
  selector: 'form-table',
  templateUrl: './form-table.component.html',
  styleUrls: ['./form-table.component.scss']
})
export class FormTableComponent extends BaseComponent implements OnInit, OnChanges, OnDestroy {
  @Input() formType!: FormType;
  @Input() displayedColumns!: string[];
  @Input() filter?: string;

  @Output() error = new EventEmitter<string>();
  @Output() rowClick = new EventEmitter<Form | null>();

  form?: Form | null;
  forms!: Form[];
  forms$!: Observable<Form[]>;
  formsSubs!: Subscription;

  allForms: Form[] = [];
  filteredForms: Form[] = [];

  showClosed!: boolean;

  subEditing!: Subscription;
  subRefreshForms!: Subscription;
  sortDefault: Sort = { active: 'sequenceNumber', direction: 'asc' };
  pendingSelect: number | null = null;

  @ViewChild(MatSort) sort!: MatSort;

  dataSource!: MatTableDataSource<Form>;
  service!: EbService | PpstbService;

  constructor(
    protected override injector: Injector,
    public formService: FormService,
    private ebService: EbService,
    private ppstbService: PpstbService
  ) {
    super(injector);
  }

  ngOnInit(): void {
    this.service = this.formType == this.formTypeEnum.PPSTB ? this.ppstbService : this.ebService;
  }

  ngOnChanges(changes: SimpleChanges): void {
    //this.loading.emit(true);
    this.loadForms();
    if ('filter' in changes) {
      this.filterForms();
    }
  }

  /**
   * Loads forms from the store, prepares searchable derived fields, and reapplies the active filter.
   */
  loadForms() {
    this.forms$ = this.store.select(state => state.Forms.data);
    this.formsSubs = this.forms$.subscribe(async data => {
      if (data?.length) {
        this.allForms = data.filter(x => x.type == this.formType);
        // Derive the icon count from the active version chain instead of the aggregate count that includes canceled versions.
        this.allForms.forEach(form => form.amendmentCount = this.getAmendmentCount(form));
        this.filteredForms = [...this.allForms];
        // Populate derived search fields before filtering refreshed PPSTB/EB rows.
        this.systemAffected();
        this.filterForms();

        const openDocument = await this.checkOpenDocument<Form>(this.formType, data);
        if (openDocument && this.form?.id != openDocument.document.id) {
          this.setFormDirty(false);
          this.showClosed = openDocument.closed;
          this.getValues(openDocument.document);
        }
      }
    });
  }

  /**
   * Counts non-canceled amendment versions represented by the active form-version chain.
   * @param form Form whose active version and previous versions should be inspected.
  * @returns The number of non-canceled amendment versions.
  */
  private getAmendmentCount(form: Form): number {
    let version: FormVersion | undefined = form.formVersion;
    let amendmentCount = 0;
    const visitedVersions = new Set<number>();

    while (version?.previousVersionID != null) {
      if (version.id && visitedVersions.has(version.id))
        break;

      if (version.id)
        visitedVersions.add(version.id);

      if (version.statusID !== FormStatusEnum.Canceled)
        amendmentCount++;

      version = version.previousVersion;
    }

    return amendmentCount;
  }

  override ngOnDestroy(): void {
    this.subEditing?.unsubscribe();
    this.subRefreshForms?.unsubscribe();
    this.formsSubs?.unsubscribe();
  }

  /**
   * Rebuilds the form list while reapplying the current search term after store refreshes.
   */
  filterForms(): void {
    const currentFilter = this.filter?.toLowerCase() ?? '';
    this.filter = currentFilter;
    this.filteredForms = this.allForms.filter(x => !this.showClosed ? (x.formVersion.statusID != FormStatusEnum.Closed && x.formVersion.statusID != FormStatusEnum.Canceled) : true).filter(x =>
      x.sequenceNumber.number.trim().toLowerCase().includes(currentFilter) ||
      x.formVersion.status.name.trim().toLowerCase().includes(currentFilter) || x.formVersion.systemAffected?.toLowerCase().includes(currentFilter)
    );
    // Reapply the current filter whenever the table data is rebuilt from store data.
    this.dataSource = new MatTableDataSource(this.filteredForms.sort((a, b) => utils.sort(a.sequenceNumber.number, b.sequenceNumber.number, false)));
    this.setSortingAccessor();
  }

  /**
   * Selects a form row, or clears the current selection when the selected row is clicked again.
   * @param form Clicked form row.
   */
  async getValues(form: Form) {
    if (await this.canDeactivate()) {
      // Match SCF and RSF side-table behavior by treating a repeated click as deselection.
      if (this.form?.id == form.id) {
        this.form = null;
        this.setOpenDocument(form.type);
        this.rowClick.emit(null);
        return;
      }

      this.form = form;
      this.setOpenDocument(form.type, form.id);
      //this.loading.emit(true);
      this.rowClick.emit(form);
    }
  }

  setSortingAccessor() {
    // Define the sorting accessor
    this.dataSource.sortingDataAccessor = (item, property) => {
      switch (property) {
        case 'sequenceNumber':
          return item.sequenceNumber.number; // Access the nested property for sequence number
        case 'status':
          return item.formVersion.status.name; // Access the nested property for status name
        case 'systemAffected':
          return item.formVersion.systemAffected ?? ''; // Access the nested property for system affected
        default:
          return (item as any)[property]; // Fallback to the default property
      }
    };
    this.setSort<Form>(this.dataSource, this.sort);
  }

  clear() {
  }

  systemAffected() {
    switch (this.formType) {
      case FormType.PPSTB: {
        this.filteredForms.map(element => {
          const systemAffected = [];
          element.description = element.formVersion.ppstb?.justificationForBypass;
          if (element.formVersion.ppstb?.accelerator) {
            if (element.formVersion.ppstb.ppstbAcceleratorLocations.length > 0) {
              element.formVersion.ppstb.ppstbAcceleratorLocations.map(location => {
                systemAffected.push(location.resource?.name);
              });
            } else { systemAffected.push('Accelerator'); }
          }
          if (element.formVersion.ppstb?.beamline) {
            if (element.formVersion.ppstb.ppstbShutters.length > 0) {
              element.formVersion.ppstb.ppstbShutters.map(shutter => {
                systemAffected.push(shutter.resource?.name);
              });
            } else { systemAffected.push('Beamline'); }
          }
          if (element.formVersion.ppstb?.topOff) {
            systemAffected.push('Top-Off');
          }
          if (element.formVersion.ppstb?.radiationMonitoringSystem) {
            systemAffected.push('Radiation Monitoring System');
          }
          if (element.formVersion.ppstb?.ppstbOtherSystems.length) {
            element.formVersion.ppstb.ppstbOtherSystems.map(otherSystem => {
              systemAffected.push(otherSystem.text);
            });
          }
          element.formVersion.systemAffected = systemAffected.join(', ');
        });
        break;
      }
      case FormType.EBF: {
        this.filteredForms.map(element => {
          const systemAffected = [];
          element.description = element.formVersion.eB2?.equipmentOrFunctionForBypassAndReason;
          if (element.formVersion.eB2?.accelerator) {
            if (element.formVersion.eB2.eB2AcceleratorLocations.length > 0) {
              element.formVersion.eB2.eB2AcceleratorLocations.map(location => {
                systemAffected.push(location.resource?.name);
              });
            } else { systemAffected.push('Accelerator'); }
          }
          if (element.formVersion.eB2?.beamline) {
            if (element.formVersion.eB2.eB2Shutters.length > 0) {
              element.formVersion.eB2.eB2Shutters.map(shutter => {
                systemAffected.push(shutter.resource?.name);
              });
            } else { systemAffected.push('Beamline'); }
          }
          if (element.formVersion.eB2?.eB2OtherSystems.length) {
            element.formVersion.eB2.eB2OtherSystems.map(otherSystem => {
              systemAffected.push(otherSystem.text);
            });
          }
          element.formVersion.systemAffected = systemAffected.join(', ');
        });
        break;
      }
      default: break;
    }
  }

}
